import React, { useEffect, useState } from "react";
import { hqApi } from "@/hq/hqApi";
import { Mail, Send, Zap, CheckCircle2, Clock } from "lucide-react";
import { asText } from "@/hq/asText";

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

/**
 * Phase 3I.4S — Follow-ups list rendered inside PitchDetailModal.
 * Test rows are excluded server-side.
 */
export default function FollowUpsSection({ commId, onGenerate, refreshKey = 0 }) {
  const [items, setItems] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (!commId) return;
    hqApi.get(`/control/pitches/${commId}/followups`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [commId, refreshKey]);

  if (!commId) return null;

  return (
    <div className="space-y-2" data-testid="followups-section">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#FF6A00]">Follow-Ups</div>
        <button
          type="button"
          onClick={onGenerate}
          data-testid="followup-generate-btn"
          className="px-3 py-1.5 rounded-full bg-[#FF6A00] text-white text-[10px] tracking-[0.16em] uppercase font-black hover:bg-[#e35c00] inline-flex items-center gap-1"
        ><Zap size={11}/> Generate follow-up</button>
      </div>

      {!items ? (
        <div className="text-[12px] text-[#8A8A8A]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-[12px] text-[#8A8A8A]" data-testid="followups-empty">
          No follow-ups sent yet.
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((f, idx) => (
            <li key={f.id}
                data-testid={`followup-row-${f.id}`}
                className="rounded-xl border border-[#EAEAEA] p-3 bg-white">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10px] tracking-[0.14em] uppercase font-black text-[#8A8A8A]">
                    Follow-Up #{idx + 1}
                    {f.status === "draft" && <span className="ml-2 text-[#8A2E00]">DRAFT</span>}
                    {f.status === "sent"  && <span className="ml-2 text-[#0F7B3E]">SENT</span>}
                    {f.status === "failed" && <span className="ml-2 text-[#C1350B]">FAILED</span>}
                  </div>
                  <div className="text-[13px] font-black truncate">{asText(f.subject) || "—"}</div>
                  <div className="text-[11px] text-[#8A8A8A] flex flex-wrap gap-x-3 gap-y-0.5">
                    <span><Clock size={9} className="inline"/> {fmt(f.sent_at || f.created_at)}</span>
                    <span><Mail size={9} className="inline"/> Opens {f.open_count || 0}</span>
                    <span>Clicks {f.click_count || 0}</span>
                    {f.interest_cta_clicked_at && (
                      <span data-testid={`followup-cta-click-${f.id}`}>CTA clicked</span>
                    )}
                    {f.interested_at && (
                      <span className="text-[#0F7B3E] font-bold" data-testid={`followup-confirmed-${f.id}`}>
                        <CheckCircle2 size={9} className="inline"/> Interested confirmed
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setOpenId(openId === f.id ? null : f.id)}
                  data-testid={`followup-toggle-${f.id}`}
                  className="text-[10px] tracking-[0.16em] uppercase font-black px-2 py-1 rounded-full border border-[#EAEAEA] text-[#4A4A4A] hover:border-[#0A0A0A]"
                >{openId === f.id ? "Hide" : "View"}</button>
              </div>
              {openId === f.id && f.rendered_html && (
                <div className="mt-2">
                  <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#0F7B3E] mb-1">
                    Exact sent email
                  </div>
                  <iframe
                    title={`Follow-up ${f.id}`}
                    srcDoc={f.rendered_html}
                    sandbox=""
                    data-testid={`followup-iframe-${f.id}`}
                    className="w-full h-[420px] rounded-xl border border-[#EAEAEA] bg-white"
                  />
                </div>
              )}
              {openId === f.id && !f.rendered_html && (
                <div className="mt-2 text-[11px] text-[#8A8A8A]">
                  Draft only — no sent HTML archived yet.
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
